import React, { useReducer, useLayoutEffect } from "react";
import { v4 } from "uuid";
import cloneDeep from "lodash/cloneDeep";

import { TreeContext } from "./TreeContext";
import { StyledTree } from "./Tree.style";
import { Folder } from "./TreeFolder";
import { File } from "./TreeFile";
import {
  searchDFS,
  createFile,
  createFolder,
  useDidMountEffect
} from "../utils";

export interface TreeNode {
  id?: string;
  name: string;
  type: 'file' | 'folder';
  files?: TreeNode[];
  parent?: TreeNode;
  processing?: boolean;
  selected?: boolean;
  onSelect?: (checked: boolean) => void;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: (deleteCallback: () => void) => void;
  onCreateFolder?: (name: string, createCallback: () => void) => void;
  onCreateFile?: (name: string, createCallback: () => void) => void;
  onRename?: (name: string) => Promise<void>;
  onDownload?: () => void;
}

interface TreeActionPayload {
  id?: string;
  name?: string;
}

interface SetDataAction {
  type: "SET_DATA";
  payload: TreeNode[];
}

interface TreeModificationAction {
  type: "CREATE_FILE" | "EDIT_FILE" | "DELETE_FILE" | "CREATE_FOLDER" | "RENAME_FOLDER" | "DELETE_FOLDER";
  payload: TreeActionPayload;
}

type TreeAction = SetDataAction | TreeModificationAction;

interface TreeContextType {
  isImparative: boolean;
  state: TreeNode[];
  dispatch: React.Dispatch<TreeAction>;
  onNodeClick: (node: { state: TreeNode[]; name: string; parent?: TreeNode; type: string; level?: number; path?: string }) => void;
}

interface SearchDFSResult {
  item: TreeNode | null;
  parent: TreeNode | null;
}

const isTreeActionPayload = (payload: TreeActionPayload | TreeNode[]): payload is TreeActionPayload => {
  return !Array.isArray(payload) && 'id' in payload;
};

const reducer = (state: TreeNode[], action: TreeAction) => {
  if (action.type === "SET_DATA") {
    return action.payload;
  }

  let newState = cloneDeep(state);
  let node: TreeNode | null = null;
  let parent: TreeNode | null = null;
  
  if (action.payload.id) {
    const foundNode: SearchDFSResult = searchDFS({
      data: newState,
      cond: (item: TreeNode) => {
        return item.id === action.payload.id;
      }
    });
    parent = foundNode.parent;
    node = foundNode.item;
  }

  switch (action.type) {
    case "CREATE_FILE": {
      if (!node || !node.files) return newState;
      node.files.push(createFile({ name: action.payload.name || '' }));
      return newState;
    }
    case "EDIT_FILE": {
      if (!node) return newState;
      node.name = action.payload.name || '';
      return newState;
    }
    case "DELETE_FILE": {
      if (!parent) {
        newState = newState.filter((file: TreeNode) => file.id !== action.payload.id);
        return newState;
      } else if (parent.files) {
        parent.files = parent.files.filter(
          (file: TreeNode) => file.id !== action.payload.id
        );
      }
      return newState;
    }
    case "CREATE_FOLDER": {
      if (!node || !node.files) return newState;
      node.files.push(createFolder({ name: action.payload.name || '' }));
      return newState;
    }
    case "RENAME_FOLDER": {
      if (!node) return newState;
      node.name = action.payload.name || '';
      return newState;
    }
    case "DELETE_FOLDER": {
      if (!parent) {
        newState = newState.filter((item: TreeNode) => item.id !== action.payload.id);
      } else if (parent.files) {
        parent.files = parent.files.filter(
          (item: TreeNode) => item.id !== action.payload.id
        );
      }
      return newState;
    }
    default:
      return state;
  }
};

interface TreeProps {
  children?: React.ReactNode;
  data?: TreeNode[];
  onNodeClick?: (node: { state: TreeNode[]; name: string; parent?: TreeNode; type: string; level?: number; path?: string }) => void;
  onUpdate?: (state: TreeNode[]) => void;
}

const Tree = ({ children, data, onNodeClick, onUpdate }: TreeProps) => {
  const [state, dispatch] = useReducer(reducer, data || []);

  useLayoutEffect(() => {
    dispatch({ type: "SET_DATA", payload: data || [] });
  }, [data]);

  useDidMountEffect(() => {
    onUpdate && onUpdate(state);
  }, [state]);

  const makeComponents = React.useCallback(
    (data: TreeNode[]) => {
      return data.map((item: TreeNode) => {
        item.id = v4();

        // root files
        if (item.type === "file") {
          return <File key={item.id} id={item.id} name={item.name} selected={item.selected} processing={item.processing} onSelect={item.onSelect} onClick={item.onClick} onEdit={item.onEdit} onDelete={item.onDelete} onRename={item.onRename} />;
        }

        return (
          <Folder id={item.id} name={item.name} selected={item.selected} onSelect={item.onSelect} onEdit={item.onEdit} onDelete={item.onDelete} onCreateFolder={item.onCreateFolder} onCreateFile={item.onCreateFile} onRename={item.onRename}>
            {item.files &&
              item.files.map((file: TreeNode) => {
                file.id = v4();
                file.parent = item; // reference to the parent folder
                if (file.type === "folder") {
                  return makeComponents([file]);
                }
                return (
                  <File
                    parent={item}
                    id={file.id}
                    key={file.id}
                    name={file.name}
                    selected={file.selected}
                    processing={file.processing}
                    onSelect={file.onSelect}
                    onClick={file.onClick}
                    onEdit={file.onEdit}
                    onDelete={file.onDelete}
                    onRename={file.onRename}
                    onDownload={file.onDownload}
                  />
                );
              })}
          </Folder>
        );
      });
    },
    [state]
  );

  const isImparative = data && !children;

  return (
    <TreeContext.Provider
      value={{
        isImparative: isImparative || false,
        state,
        dispatch,
        onNodeClick: (node) => {
          onNodeClick && onNodeClick(node);
        }
      }}
    >
      <StyledTree>{isImparative ? makeComponents(state) : children}</StyledTree>
    </TreeContext.Provider>
  );
};

Tree.File = File;
Tree.Folder = Folder;

export default Tree;
